/**
 * ecount-sync/render — 실사 탭의 그리기. 순수 함수(상태 → HTML 문자열).
 *
 * 단계 줄(renderSteps) · 자료 · 파일 폼(renderUploadForm — 파일 칸은 점선 상자 es-dropzone) · 검토 결과(renderPreview — 자료로 갈라 줄 표 셋) · 보고(renderReport) · 업로드 내역(renderUploads) · 연결 안 된 품목(renderUnlinked).
 * 동사는 검토 → 반영 둘(현민 2026-10-13). "올리기" · "미리보기" 는 화면 글자에 안 쓴다.
 * CSS 접두 `es-` = ecount-sync. 카드 · 표는 제품 시안의 `pc-card` · `pv-table` 톤. 글자 셋 — 머리글 · 본문 13px 진한(`pl-main`) · 보조 12px 회색.
 *
 * 2 시그니처 → 3 몸통(이 커밋).
 */
import type {
  EcountUploadView, EEcountSheet, ItemPreviewLine, PurchasePreviewLine, StockPreviewLine, SyncPreview, SyncReport, UnmappedItemView,
} from '../../api/client';
import { E_ECOUNT_SHEETS } from '../../api/client';
import { ROW_NO_HEAD, rowNoCell } from '../../components/row-no';
import { escapeHtml, fmtDate, fmtDateTime, fmtNumber } from '../../lib/cells';
import { LINK_PLAN_WORD, MATCH_KIND_WORD, SHEET_WORD, VERDICT_WORD, WARNING_WORD } from './types';
import type { EcountSyncData } from './types';

// ─── 라벨 ───────────────────────────────────────────────────────────
/** 자료마다 파일 칸의 안내 — 이카운트 어느 화면에서 내보낸 파일인가 */
const FILE_HINT: Record<EEcountSheet, string> = {
  item:     '재고Ⅰ › 기초등록 › 품목등록 — 사용구분 "전체" 로 조회해 내보낸 xlsx',
  stock:    '재고Ⅰ › 재고현황 — 내보낸 xlsx (이 자료에 없는 품목은 재고 0 으로 맞춘다)',
  purchase: '재고Ⅰ › 구매관리 › 구매현황 — 라인별 · 양식 "번호 및 파트넘버" 로 내보낸 xlsx',
};
/** 보고의 칸 이름 — 0 인 칸은 안 보인다 */
const REPORT_WORD: Record<Exclude<keyof SyncReport, 'upload_id'>, string> = {
  inserted: '새것', updated: '다름(고침)', skipped: '같음', unmapped: '연결 안 됨',
  products_created: 'product 만듦', products_linked: 'product 이음',
  units_added: '실물 더함', units_retired: '실물 뺌', units_costed: '원가 붙임',
};
const VERDICT_CHIP: Record<ItemPreviewLine['verdict'], string> = { new: 'chip-success', same: 'chip-muted', changed: 'chip-warning', missing: 'chip-primary' };

// ─── 단계 줄 ────────────────────────────────────────────────────────
/** 단계 셋 — ① 자료 · 파일 → ② 검토 → ③ 반영. 지금 단계는 preview · report 의 유무로 안다 */
export function renderSteps(data: EcountSyncData): string {
  const current: number = data.report !== null ? 3 : data.preview !== null ? 2 : 1;
  const step = (n: number, label: string): string => `<span class="es-step${n === current ? ' is-on' : n < current ? ' is-done' : ''}"><b>${n}</b>${label}</span>`;
  return `<nav class="es-steps">${step(1, '자료 · 파일')}<span class="es-step-arrow">→</span>${step(2, '검토')}<span class="es-step-arrow">→</span>${step(3, '반영')}</nav>`;
}

// ─── 자료 · 파일 폼 ─────────────────────────────────────────────────
/** 자료 · 파일 폼 — 자료 세그먼트 · 내보낸 날 · 파일 상자(구매현황은 둘) · [검토]. preview 가 있으면 접힌 한 줄 + [다시 고르기] */
export function renderUploadForm(data: EcountSyncData): string {
  if (data.preview !== null) return renderPickedLine(data.preview);

  const { form } = data;
  const segments = E_ECOUNT_SHEETS.map((sheet) =>
    `<button type="button" class="pc-type${sheet === form.sheet ? ' is-on' : ''}" data-action="es-sheet" data-sheet="${sheet}">${SHEET_WORD[sheet]}</button>`).join('');
  const slipZone = form.sheet !== 'purchase' ? '' : renderDropzone('es-slip-file', '"현황" 양식 파일', '같은 조회를 양식 "현황" 으로 한 번 더 내보낸 xlsx — 전표번호가 여기 있다', form.slipFile);
  return `
    <section class="form-card pc-card es-form">
      <div class="form-card-head"><h2>자료 · 파일</h2><small>순서대로 — 품목등록 → 재고현황 → 구매현황. 앞 자료가 반영돼 있어야 다음 자료를 검토할 수 있다. 검토는 아무것도 바꾸지 않는다</small></div>
      <div class="es-form-row">
        <label class="form-field"><span class="form-label">자료</span><div class="pc-types">${segments}</div></label>
        <label class="form-field"><span class="form-label">내보낸 날</span>
          <input type="date" data-action="es-exported-at" value="${escapeHtml(form.exportedAt)}"></label>
      </div>
      <div class="es-form-row es-zones">
        ${renderDropzone('es-file', '파일', FILE_HINT[form.sheet], form.file)}${slipZone}
      </div>
      <div class="es-actions">
        <span class="text-muted">검토를 누르면 파일을 읽어 무엇이 바뀌는지 보인다 — 표는 아직 안 바뀐다. 빈 칸이 있으면 무엇이 비었는지 알려 준다</span>
        <span class="pv-spacer"></span>
        <button type="button" class="btn-primary es-review" data-action="es-upload">검토</button>
      </div>
    </section>`;
}

/** 파일 상자 — 점선 상자 하나. 클릭이면 숨은 input, 떨어뜨리면 drop. 고른 파일은 상태(form)에서 그린다 — input 의 표시에 기대지 않는다 */
function renderDropzone(action: string, label: string, hint: string, fileOrNull: File | null): string {
  const picked = fileOrNull === null
    ? `<span class="es-drop-empty">여기에 xlsx 를 떨어뜨리거나 눌러서 고른다</span>`
    : `<span class="es-drop-file"><b>${escapeHtml(fileOrNull.name)}</b><small>${escapeHtml(fmtNumber(Math.round(fileOrNull.size / 1024)))} KB</small></span>`;
  return `
    <label class="es-dropzone${fileOrNull === null ? '' : ' has-file'}" data-action="es-drop" data-file-action="${action}">
      <span class="form-label">${escapeHtml(label)} <small class="text-muted">— ${escapeHtml(hint)}</small></span>
      ${picked}
      <input type="file" accept=".xlsx" data-action="${action}" hidden>
    </label>`;
}

/** preview 가 있을 때의 접힌 한 줄 — 어느 자료 · 내보낸 날 · 업로드 번호 + [다시 고르기] */
function renderPickedLine(preview: SyncPreview): string {
  return `
    <section class="form-card pc-card es-form es-picked">
      <div class="form-card-head"><h2>${SHEET_WORD[preview.sheet]} <small>내보낸 날 ${escapeHtml(fmtDate(preview.exported_at))} · 업로드 #${escapeHtml(preview.upload_id)}</small></h2></div>
      <span class="pv-spacer"></span>
      <button type="button" class="btn-ghost" data-action="es-reset">다시 고르기</button>
    </section>`;
}

// ─── 검토 결과 ──────────────────────────────────────────────────────
/** 검토 결과 — 숫자 묶음(판정 넷 + 자료마다의 수) · 줄 표 · [반영]. report 가 있으면 반영 버튼 대신 보고 */
export function renderPreview(data: EcountSyncData): string {
  const { preview, report } = data;
  if (preview === null) return '';

  const footer = report === null
    ? `<div class="es-actions"><span class="text-muted">반영은 한 번 — 원본 표 · 연결 · 실물이 바뀐다</span><span class="pv-spacer"></span><button type="button" class="btn-primary" data-action="es-apply">반영</button></div>`
    : renderReport(report);
  return `
    <section class="form-card pc-card es-preview">
      <div class="form-card-head"><h2>검토 결과</h2><small>${escapeHtml(fmtNumber(preview.lines.length))}줄 — 판정은 지금 표 기준으로 다시 센 것</small></div>
      ${renderCounts(preview)}
      ${renderLines(preview)}
      ${footer}
    </section>`;
}

/** 숫자 묶음 — 판정 넷은 공통, 그 뒤는 자료마다 */
function renderCounts(preview: SyncPreview): string {
  const common = `${stat('새것', preview.counts.new)}${stat('같음', preview.counts.same)}${stat('다름', preview.counts.changed)}${stat('빠짐', preview.counts.missing)}`;
  switch (preview.sheet) {
    case 'item':     return `<div class="es-stats">${common}${stat('연결 안 됨', preview.counts.unmapped)}</div>`;
    case 'stock':    return `<div class="es-stats">${common}${stat('실물 더함', preview.counts.add_units)}${stat('실물 뺌', preview.counts.remove_units)}${stat('경고', preview.counts.warnings)}</div>`;
    case 'purchase': return `<div class="es-stats">${common}${stat('원가 붙는 실물', preview.counts.units_costed)}${stat('원가 없는 실물', preview.counts.units_without_cost)}</div>`;
  }
}

function stat(label: string, value: number): string {
  return `<div class="es-stat${value === 0 ? ' is-zero' : ''}"><b>${escapeHtml(fmtNumber(value))}</b><span>${escapeHtml(label)}</span></div>`;
}

/** 줄 표 — 자료로 갈린다. 같음 줄은 접는다(수백 줄이라) — 새것 · 다름 · 빠짐 · 할 일 있는 줄만 */
function renderLines(preview: SyncPreview): string {
  switch (preview.sheet) {
    case 'item':     return renderItemLines(preview.lines);
    case 'stock':    return renderStockLines(preview.lines);
    case 'purchase': return renderPurchaseLines(preview.lines);
  }
}

function renderItemLines(lines: ItemPreviewLine[]): string {
  const shown = lines.filter((line) => line.verdict !== 'same' || line.link !== 'keep');
  const rows = shown.map((line, index) => `
    <tr>
      ${rowNoCell(0, index)}
      <td class="pl-id mono">${escapeHtml(line.item_code)}</td>
      <td>${verdictChip(line.verdict)}</td>
      <td class="pl-main mono"><b>${escapeHtml(line.part_number)}</b></td>
      <td>${escapeHtml(LINK_PLAN_WORD[line.link])}</td>
      <td class="pl-sub">${line.product === null ? '' : escapeHtml(line.product.hclt_number)}</td>
      <td class="pl-sub">${escapeHtml(line.changed_columns.join(' · '))}</td>
    </tr>`);
  return table(['품목코드', '판정', '파트넘버', '할 일', 'product', '바뀐 열'], rows, lines.length);
}

function renderStockLines(lines: StockPreviewLine[]): string {
  const shown = lines.filter((line) => line.add > 0 || line.remove > 0 || line.warning !== null || line.verdict === 'missing');
  const rows = shown.map((line, index) => `
    <tr>
      ${rowNoCell(0, index)}
      <td class="pl-id mono">${escapeHtml(line.item_code)}</td>
      <td>${verdictChip(line.verdict)}</td>
      <td class="pl-main num">${escapeHtml(fmtNumber(line.quantity))}</td>
      <td class="pl-main num">${escapeHtml(fmtNumber(line.held))}</td>
      <td class="pl-main num">${line.add === 0 ? '' : `+${escapeHtml(fmtNumber(line.add))}`}</td>
      <td class="pl-main num">${line.remove === 0 ? '' : `−${escapeHtml(fmtNumber(line.remove))}`}</td>
      <td class="pl-sub">${line.link === null ? '연결 줄 없음' : escapeHtml(MATCH_KIND_WORD[line.link])}</td>
      <td>${line.warning === null ? '' : `<span class="chip chip-warning">${escapeHtml(WARNING_WORD[line.warning])}</span>`}</td>
    </tr>`);
  return table(['품목코드', '판정', '이카운트', '보유', '더함', '뺌', '연결', '경고'], rows, lines.length);
}

function renderPurchaseLines(lines: PurchasePreviewLine[]): string {
  const shown = lines.filter((line) => line.verdict !== 'same');
  const rows = shown.map((line, index) => `
    <tr>
      ${rowNoCell(0, index)}
      <td class="pl-id mono">${escapeHtml(line.slip_no)} #${line.line_seq}</td>
      <td>${verdictChip(line.verdict)}</td>
      <td class="pl-main mono">${escapeHtml(line.item_code)}</td>
      <td class="pl-main num">${line.unit_price === null ? '<span class="text-muted">내부 입고</span>' : escapeHtml(fmtNumber(line.unit_price))}</td>
    </tr>`);
  return table(['전표', '판정', '품목코드', '단가'], rows, lines.length);
}

function verdictChip(verdict: ItemPreviewLine['verdict']): string {
  return `<span class="chip ${VERDICT_CHIP[verdict]}">${VERDICT_WORD[verdict]}</span>`;
}

/** 표 틀 — 보일 줄이 없으면 "전부 같음" 한 줄. 접힌 줄 수를 머리에 */
function table(heads: string[], rows: string[], total: number): string {
  const hidden = total - rows.length;
  const note = hidden === 0 ? '' : `<p class="pv-lead">같음 · 할 일 없는 ${escapeHtml(fmtNumber(hidden))}줄은 접었다</p>`;
  const body = rows.length === 0 ? `<tr><td colspan="${heads.length + 1}" class="text-muted">보일 줄이 없다 — 전부 같음</td></tr>` : rows.join('');
  return `${note}<div class="es-table-wrap"><table class="pv-table es-table"><thead><tr>${ROW_NO_HEAD}${heads.map((h) => `<th>${h}</th>`).join('')}</tr></thead><tbody>${body}</tbody></table></div>`;
}

// ─── 보고 ───────────────────────────────────────────────────────────
/** 보고 — 반영 한 번의 숫자. 0 인 칸은 안 보인다 */
export function renderReport(report: SyncReport): string {
  const keys = Object.keys(REPORT_WORD) as (keyof typeof REPORT_WORD)[];
  const stats = keys.filter((key) => report[key] !== 0).map((key) => stat(REPORT_WORD[key], report[key])).join('');
  return `
    <div class="es-report">
      <div class="form-card-head"><h2>반영됨</h2><small>업로드 #${escapeHtml(report.upload_id)} — 변동표에서 이 번호로 거를 수 있다</small></div>
      <div class="es-stats">${stats === '' ? '<span class="text-muted">바뀐 것이 없다 — 전부 같음</span>' : stats}</div>
    </div>`;
}

// ─── 업로드 내역 ────────────────────────────────────────────────────
/** 업로드 내역 — 자료 · 내보낸 날 · 올린 사람 · 반영 · 숫자. 줄의 [보기] 가 검토 결과를 연다 */
export function renderUploads(data: EcountSyncData): string {
  const rows = data.uploads.map((upload) => renderUploadRow(upload));
  const body = rows.length === 0 ? '<tr><td colspan="7" class="text-muted">아직 올린 자료가 없다</td></tr>' : rows.join('');
  return `
    <section class="form-card pc-card es-uploads">
      <div class="form-card-head"><h2>업로드 내역</h2><small>최근 먼저 · 반영 안 된 업로드는 [보기] 로 이어서 검토 → 반영</small><span class="pv-spacer"></span><button type="button" class="btn-ghost" data-action="es-empty-price-csv" title="반영된 구매현황에서 단가가 빈 줄 — 제품팀이 이카운트에서 채울 목록">빈 단가 줄 CSV</button></div>
      <div class="es-table-wrap"><table class="pv-table es-table">
        <thead><tr><th>#</th><th>자료</th><th>내보낸 날</th><th>올린 사람</th><th>올린 때</th><th>반영</th><th></th></tr></thead>
        <tbody>${body}</tbody>
      </table></div>
    </section>`;
}

function renderUploadRow(upload: EcountUploadView): string {
  const sheetWord = upload.sheet === 'serial' ? '시리얼' : SHEET_WORD[upload.sheet];
  const applied = upload.applied_at === null
    ? '<span class="chip chip-warning">반영 전</span>'
    : `<span class="chip chip-success">반영</span> <span class="pl-sub">${escapeHtml(fmtDateTime(upload.applied_at))} · 새것 ${fmtNumber(upload.inserted)} · 다름 ${fmtNumber(upload.updated)} · 같음 ${fmtNumber(upload.skipped)}</span>`;
  return `
    <tr>
      <td class="pl-id mono">${escapeHtml(upload.id)}</td>
      <td class="pl-main"><b>${escapeHtml(sheetWord)}</b> <span class="pl-sub">${escapeHtml(fmtNumber(upload.row_count))}줄</span></td>
      <td class="pl-main">${escapeHtml(fmtDate(upload.exported_at))}</td>
      <td class="pl-sub">${escapeHtml(upload.uploaded_by)}</td>
      <td class="pl-sub">${escapeHtml(fmtDateTime(upload.uploaded_at))}</td>
      <td>${applied}</td>
      <td><button type="button" class="btn-ghost" data-action="es-open-upload" data-upload-id="${escapeHtml(upload.id)}">보기</button></td>
    </tr>`;
}

// ─── 연결 안 된 품목 ────────────────────────────────────────────────
/** 연결 안 된 품목 — 품목코드 · 품목명 · 파트넘버 · 디스크립션 · 컨디션 + product id 입력과 [잇기] */
export function renderUnlinked(data: EcountSyncData): string {
  const withStock = data.unlinked.filter((item) => item.stock_quantity !== null && item.stock_quantity > 0).length;
  const rows = data.unlinked.map((item, index) => renderUnlinkedRow(item, index));
  const body = rows.length === 0 ? '<tr><td colspan="8" class="text-muted">연결 안 된 품목이 없다</td></tr>' : rows.join('');
  return `
    <section class="form-card pc-card es-unlinked">
      <div class="form-card-head"><h2>연결 안 된 품목 <small>${escapeHtml(fmtNumber(data.unlinked.length))} · 재고 있는 것 ${escapeHtml(fmtNumber(withStock))}</small></h2><small>파트넘버가 없거나 카테고리를 못 갈라 product 에 못 이은 품목. 재고 있는 것부터 — 제품 목록에서 product 번호(id)를 찾아 잇는다, 다음 재고현황 반영이 이 줄을 받는다</small></div>
      <div class="es-table-wrap"><table class="pv-table es-table">
        <thead><tr>${ROW_NO_HEAD}<th>품목코드</th><th>이카운트 재고</th><th>품목명</th><th>파트넘버</th><th>디스크립션</th><th>컨디션</th><th>product id</th></tr></thead>
        <tbody>${body}</tbody>
      </table></div>
    </section>`;
}

function renderUnlinkedRow(item: UnmappedItemView, no: number): string {
  return `
    <tr data-item-code="${escapeHtml(item.item_code)}">
      ${rowNoCell(no, 0)}
      <td class="pl-id mono">${escapeHtml(item.item_code)}</td>
      <td class="pl-main num">${item.stock_quantity === null ? '<span class="text-muted">-</span>' : escapeHtml(fmtNumber(item.stock_quantity))}</td>
      <td class="pl-main">${escapeHtml(item.item_name)}</td>
      <td class="pl-main mono"><b>${escapeHtml(item.part_number)}</b></td>
      <td class="pl-sub">${escapeHtml(item.description)}</td>
      <td class="pl-sub">${escapeHtml(item.condition_name)}</td>
      <td class="es-link-cell"><input type="text" class="es-product-id mono" placeholder="id" inputmode="numeric"><button type="button" class="btn-ghost" data-action="es-link" data-item-code="${escapeHtml(item.item_code)}">잇기</button></td>
    </tr>`;
}
